import moment from 'moment-timezone';

// Integer scaling: *_x10 values are tenths, *_x100 values are hundredths.
export const formatScaled = (value, scale) => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return (n / scale).toFixed(scale === 10 ? 1 : 2);
};

export const RANKED_COLUMNS = [
  {
    key: 'rank',
    label: 'Rank',
    tip: 'Final position after sorting by fit, 555 decile, retention decile, newer teacher (profile created date), then a seeded random tiebreak.'
  },
  { key: 'teacher', label: 'Teacher', tip: 'Teacher name and id.' },
  {
    key: 'goals',
    band: 'Goal match',
    label: 'Matched',
    tip: 'matched_goals / total_goals. Health goals auto-match when the teacher has the required therapy/prenatal flag; general goals match via teacher goal mapping.'
  },
  {
    key: 'goal_score_x10',
    band: 'Goal match',
    label: 'Goal score (0-9)',
    tip: 'goal_score_x10 = floor(matched × 90 / total). Integer tenths, 0..90 (i.e. 0.0..9.0). 0 when the student has no goals.'
  },
  {
    key: 'days_available',
    band: 'Availability',
    label: 'Free days of next 14',
    tip: 'Distinct days in the next 14 (day 0..13) on which the teacher is free at the same wall-clock time in the student timezone.'
  },
  {
    key: 'repeat_available',
    band: 'Availability',
    label: 'Repeats weekly',
    tip: 'Teacher offers this slot weekly. Adds 10 (1.0) to the availability score.'
  },
  {
    key: 'avail_score_x10',
    band: 'Availability',
    label: 'Availability score (0-9)',
    tip: 'avail_score_x10 = floor(days × 80 / 14) + (repeat ? 10 : 0). Integer tenths, 0..90 (0.0..9.0).'
  },
  {
    key: 'fit_score',
    band: 'Fit',
    label: 'Fit score (0-900)',
    tip: 'fit_score = goal_score_x10 × 6 + avail_score_x10 × 4. Integer 0..900 (equals Goal×60 + Avail×40 on one-decimal scores). Primary sort key.'
  },
  {
    key: 'finished_trials',
    band: 'Tie-breakers',
    label: 'Finished trials',
    tip: 'Finished 1-on-1 YOGA trial sessions. Fewer than 3 means new teacher: effective points forced to 20.'
  },
  {
    key: 'points_balance',
    band: 'Tie-breakers',
    label: '555 balance',
    tip: 'Raw teacher_trial_points.current_balance (Conversion Points). No row means 0.'
  },
  {
    key: 'effective_points_x100',
    band: 'Tie-breakers',
    label: 'Effective 555 points',
    tip: 'effective_points_x100: 20.00 if finished trials < 3, else the balance. Integer hundredths (2350 = 23.50).'
  },
  {
    key: 'points_decile',
    band: 'Tie-breakers',
    label: '555 decile',
    tip: 'Decile 0..9 of effective points among Stage-0 survivors (ASC, tiebreak teacher_id ASC; floor(pos × 10 / N)). Second sort key.'
  },
  {
    key: 'retention_score_x100',
    band: 'Tie-breakers',
    label: 'Retention score',
    tip: 'retention_score_x100 = retention points × 100 / unique students, from the nightly batch. Integer hundredths. AVG badge = platform average used (new teacher / no data).'
  },
  {
    key: 'retention_decile',
    band: 'Tie-breakers',
    label: 'Retention decile',
    tip: 'Decile 0..9 of retention score among Stage-0 survivors. Third sort key.'
  },
  {
    key: 'live_date',
    band: 'Tie-breakers',
    label: 'Live date',
    tip: 'Profile live at (IST), or the profile created date while it is unset. Fourth sort key: newer teacher ranks higher.'
  },
  {
    key: 'decided_by',
    band: 'Outcome',
    label: 'Placed by',
    tip: 'Which sort key separated this teacher from the next one: fit, points, retention, newer, random, or last.'
  },
  {
    key: 'shown_status',
    band: 'Outcome',
    label: 'Where shown',
    tip: 'Rank 1..3 shown, 4..6 see_more, else see_all. Other server values (e.g. excluded) are shown as-is.'
  },
  {
    key: 'reason',
    band: 'Outcome',
    label: 'Reason',
    tip: 'Reason text, generated for the top max(6, N) only.'
  }
];

export const allFilterResults = (t) => [
  ...((t.phase_a && t.phase_a.results) || []).map((r) => ({ ...r, phase: 'A' })),
  ...((t.phase_b && t.phase_b.results) || []).map((r) => ({ ...r, phase: 'B' }))
];

export const teacherFailed = (t) =>
  (t.phase_a && t.phase_a.passed === false) || (t.phase_b && t.phase_b.passed === false);

const humanizeKey = (key) =>
  key === null || key === undefined ? '' : String(key).replace(/_/g, ' ');

const labelFrom = (dict) => (key) =>
  Object.prototype.hasOwnProperty.call(dict, key) ? dict[key] : humanizeKey(key);

export const FILTER_LABELS = {
  opted_in: 'Opted in to trials',
  ops_removed: 'Not removed by Ops',
  hidden_from_onboarding: 'Visible in onboarding',
  points_balance: '555 points balance above 0',
  health_compat: 'Health-condition match (therapy/prenatal)',
  prenatal_exclusive: 'Prenatal-only teacher rule',
  test_user: 'Test / internal teacher',
  slot_available: 'Free for 60 min at the slot',
  daily_trial_cap: 'Under daily trial cap',
  gender_pref: 'Gender preference',
  western_comfort: 'Comfortable with Western students'
};

export const DECIDED_BY_LABELS = {
  fit: 'Fit score',
  points: '555 points (tie-break)',
  retention: 'Retention (tie-break)',
  newer: 'Newer teacher (tie-break)',
  random: 'Random (all tied)',
  last: 'Last in list'
};

export const SHOWN_STATUS_LABELS = {
  shown: 'Shown',
  see_more: 'See more',
  see_all: 'See all',
  excluded: 'Excluded'
};

export const FINISHED_TRIALS_SOURCE_LABELS = {
  stats: 'nightly stats',
  live: 'live lookup',
  unavailable: 'unavailable (ranked on raw balance)',
  none: 'not looked up'
};

export const filterLabel = labelFrom(FILTER_LABELS);
export const decidedByLabel = labelFrom(DECIDED_BY_LABELS);
export const shownStatusLabel = labelFrom(SHOWN_STATUS_LABELS);
export const finishedTrialsSourceLabel = labelFrom(FINISHED_TRIALS_SOURCE_LABELS);

const tenths = (v) => formatScaled(v ?? 0, 10);

export const fitSummary = (t) =>
  `Fit ${t.fit_score ?? 0} / 900 — goals ${tenths(t.goal_score_x10)}/9 (${
    t.matched_goals ?? 0
  } of ${t.total_goals ?? 0} matched), availability ${tenths(t.avail_score_x10)}/9 (${
    t.days_available ?? 0
  } of 14 days, ${t.repeat_available ? 'repeats weekly' : 'no weekly repeat'})`;

// Go zero time (no created_date) renders as empty.
export const joinedDate = (v) => (v && !String(v).startsWith('0001') ? String(v).slice(0, 10) : '');

// Runs before live_date existed carry only created_date.
export const liveDate = (t) => t.live_date || joinedDate(t.created_date);

export const placedBySummary = (t) => {
  const parts = [`Placed by: ${t.decided_by ? decidedByLabel(t.decided_by) : '—'}`];
  if (t.points_decile !== null && t.points_decile !== undefined) {
    parts.push(`decile ${t.points_decile} of 9`);
  }
  if (t.retention_decile !== null && t.retention_decile !== undefined) {
    parts.push(
      `retention decile ${t.retention_decile}${t.retention_is_platform_avg ? ' (platform avg)' : ''}`
    );
  }
  return parts.join(' · ');
};

export const teacherSentence = (t) => `${fitSummary(t)}\n${placedBySummary(t)}`;

const failedFilterNames = (t, relaxed) =>
  allFilterResults(t)
    .filter((r) => !r.passed)
    .map((r) => ({
      filter: r.filter,
      relaxed: typeof r.relaxed === 'boolean' ? r.relaxed : relaxed.includes(r.filter)
    }));

const joinLabels = (keys) => keys.map(filterLabel).join(', ');

// Plain-English walk through the filter funnel plus a per-filter failure count.
export const buildFunnel = ({ filters, pool, slot, shownCount, topN }) => {
  const f = filters || {};
  const relaxed = f.relaxed || [];
  const soft = f.phase_b_soft_active || [];
  const sentences = [
    `Of ${pool.length} approved teachers, ${f.pool_after_phase_a ?? 0} passed eligibility (Phase A).`
  ];
  if (!slot || !slot.start_epoch) {
    sentences.push('No slot was given, so only eligibility ran.');
  } else {
    const checks = [...(f.phase_b_hard || []), ...soft];
    sentences.push(
      `At the slot ${slot.student_local || slot.start_epoch} (student local), ${
        f.pool_after_phase_b ?? 0
      } of them passed the slot checks${checks.length ? ` (${joinLabels(checks)})` : ''}.`
    );
    if (soft.length === 0) {
      sentences.push('No soft filters were active, so nothing could be relaxed.');
    } else {
      sentences.push(`Soft filters active: ${joinLabels(soft)}.`);
      sentences.push(
        relaxed.length
          ? `Relaxed because the pool was thin (in order): ${joinLabels(relaxed)}.`
          : 'None of them had to be relaxed.'
      );
    }
  }
  if (topN) sentences.push(`Top ${topN} requested, ${shownCount} shown.`);
  else sentences.push(`${shownCount} shown.`);

  // Sequential funnel: a teacher is removed by the FIRST filter (in evaluation order) it fails;
  // relaxed soft filters never remove anyone. `failing` is the independent count for reference.
  const order = [...(f.phase_a || []), ...(f.phase_b_hard || []), ...soft];
  const relaxedKeys = new Set(relaxed);
  const failing = {};
  const failedBy = pool.map((t) => {
    const set = new Map();
    failedFilterNames(t, relaxed).forEach((r) => {
      if (set.has(r.filter)) return;
      set.set(r.filter, r.relaxed);
      failing[r.filter] = (failing[r.filter] || 0) + 1;
      if (r.relaxed) relaxedKeys.add(r.filter);
      if (!order.includes(r.filter)) order.push(r.filter);
    });
    return set;
  });
  let alive = failedBy.map(() => true);
  const steps = [];
  [...new Set(order)].forEach((key) => {
    const isRelaxed = relaxedKeys.has(key);
    const before = alive.filter(Boolean).length;
    let removed = 0;
    if (!isRelaxed) {
      alive = alive.map((a, i) => {
        if (!a) return a;
        if (failedBy[i].has(key)) {
          removed += 1;
          return false;
        }
        return a;
      });
    }
    steps.push({
      filter: key,
      label: filterLabel(key),
      before,
      removed,
      remaining: before - removed,
      failing: failing[key] || 0,
      relaxed: isRelaxed
    });
  });
  return { text: sentences.join(' '), steps };
};

const WARNING_TEXT = {
  finished_trials_fallback_unavailable: (n) =>
    `Finished-trial counts were unavailable for ${n} teachers; they were ranked on their raw 555 balance.`
};

// Warnings arrive as "code: detail" strings; unknown codes are humanized.
export const warningText = (w) => {
  const raw = typeof w === 'string' ? w : JSON.stringify(w);
  const m = /^([a-z0-9_]+)\s*:?\s*(.*)$/i.exec(raw);
  if (!m) return raw;
  const [, code, detail] = m;
  const num = /(\d+)/.exec(detail);
  if (WARNING_TEXT[code] && num) return WARNING_TEXT[code](num[1]);
  const human = humanizeKey(code);
  return `${human.charAt(0).toUpperCase()}${human.slice(1)}${detail ? `: ${detail}` : ''}`;
};

export const GENDER_PREF_OPTIONS = ['FEMALE', 'MALE', 'NO_PREFERENCE'];
export const STUDENT_GENDER_OPTIONS = ['FEMALE', 'MALE'];
export const ETHNICITY_OPTIONS = ['WESTERN', 'INDIAN', 'NO_MATCH'];

export const EMPTY_OVERRIDES = {
  goals: null,
  teacher_gender_preference: '',
  student_gender: '',
  student_ethnicity: '',
  country_iso: '',
  is_referral_student: '',
  is_prenatal_student: '',
  disable_thin_pool_relaxation: false
};

// Builds the request overrides object; empty fields are omitted so the server uses student data.
export const buildOverrides = (o) => {
  const out = {};
  if (Array.isArray(o.goals)) out.goals = o.goals;
  if (o.teacher_gender_preference) out.teacher_gender_preference = o.teacher_gender_preference;
  if (o.student_gender) out.student_gender = o.student_gender;
  if (o.student_ethnicity) out.student_ethnicity = o.student_ethnicity;
  if (o.country_iso.trim()) out.country_iso = o.country_iso.trim().toUpperCase();
  if (o.is_referral_student) out.is_referral_student = o.is_referral_student === 'true';
  if (o.is_prenatal_student) out.is_prenatal_student = o.is_prenatal_student === 'true';
  if (o.disable_thin_pool_relaxation) out.disable_thin_pool_relaxation = true;
  return out;
};

// Meta goals may arrive as strings or student_goal-like objects.
export const goalName = (g) => {
  if (typeof g === 'string') return g;
  if (!g) return '';
  return g.display_name || g.goal_display_name || g.name || '';
};

const yesNo = (v) => (v ? 'yes' : 'no');

export const rankedCsvRows = (ranked) =>
  (ranked || []).map((t) => ({
    rank: t.rank,
    teacher_id: t.teacher_id,
    teacher_uuid: t.teacher_uuid,
    name: t.name,
    gender: t.gender,
    goals_matched: (t.goals_matched || []).join(', '),
    matched_goals: t.matched_goals,
    total_goals: t.total_goals,
    goal_score_x10: t.goal_score_x10,
    days_available: t.days_available,
    repeat_available: yesNo(t.repeat_available),
    avail_score_x10: t.avail_score_x10,
    fit_score: t.fit_score,
    finished_trials: t.finished_trials,
    points_balance: t.points_balance,
    effective_points_x100: t.effective_points_x100,
    points_decile: t.points_decile,
    retention_score_x100: t.retention_score_x100,
    retention_is_platform_avg: yesNo(t.retention_is_platform_avg),
    retention_decile: t.retention_decile,
    created_date: joinedDate(t.created_date),
    profile_live_at: t.profile_live_at || '',
    live_date: liveDate(t),
    finished_trials_source: t.finished_trials_source,
    finished_trials_source_label: t.finished_trials_source
      ? finishedTrialsSourceLabel(t.finished_trials_source)
      : '',
    decided_by: t.decided_by,
    decided_by_label: t.decided_by ? decidedByLabel(t.decided_by) : '',
    shown_status: t.shown_status,
    shown_status_label: t.shown_status ? shownStatusLabel(t.shown_status) : '',
    reason: t.reason
  }));

const filterSummary = (t) =>
  allFilterResults(t)
    .map(
      (r) =>
        `${r.phase}:${r.filter}=${r.passed ? 'pass' : 'FAIL'}${r.reason ? ` (${r.reason})` : ''}`
    )
    .join('; ');

const filterSummaryReadable = (t) =>
  allFilterResults(t)
    .map((r) => `${filterLabel(r.filter)}: ${r.passed ? 'pass' : 'FAIL'}`)
    .join('; ');

export const poolCsvRows = (pool) =>
  (pool || []).map((t) => ({
    teacher_id: t.teacher_id,
    teacher_uuid: t.teacher_uuid,
    name: t.name,
    gender: t.gender,
    phase_a_passed: t.phase_a ? yesNo(t.phase_a.passed) : '',
    phase_b_passed: t.phase_b ? yesNo(t.phase_b.passed) : '',
    rank: t.rank || '',
    fit_score: t.fit_score,
    filters: filterSummary(t),
    filters_readable: filterSummaryReadable(t)
  }));

export const SLOT_ALIGN_ERROR = 'Slot must start on a :00 or :30 UTC boundary.';

// Parses a student-local date + HH:mm in tz; null when unparseable.
export const parseSlot = (date, time, tz) => {
  const slot = moment.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm', true, tz || 'UTC');
  return slot.isValid() ? slot : null;
};

// Slots must align to a 30-minute boundary in UTC epoch seconds.
export const slotAlignError = (date, time, tz) => {
  if (!date || !time) return '';
  const slot = parseSlot(date, time, tz);
  if (!slot) return '';
  return slot.unix() % 1800 === 0 ? '' : SLOT_ALIGN_ERROR;
};

export const teacherProfileUrl = (t) =>
  t && t.teacher_uuid ? `/teacherprofile?id=${encodeURIComponent(t.teacher_uuid)}` : '';

const DEFAULT_TOP_N = 3;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const BOOL_PARAM = { 1: 'true', 0: 'false' };

const validDate = (v) => DATE_RE.test(v) && moment(v, 'YYYY-MM-DD', true).isValid();

// Reads playground inputs from the query string; invalid values are dropped silently.
export const parseUrlState = (search) => {
  const q = new URLSearchParams(search || '');
  const get = (k) => (q.get(k) || '').trim();
  const state = { overrides: { ...EMPTY_OVERRIDES } };
  if (get('email')) state.email = get('email');
  if (validDate(get('date'))) state.slotDate = get('date');
  if (TIME_RE.test(get('time'))) state.slotTime = get('time');
  const topN = get('top_n');
  if (/^\d+$/.test(topN) && Number(topN) >= 1 && Number(topN) <= 50) state.topN = Number(topN);
  if (get('phase_a') === '1') state.phaseAOnly = true;
  // Audit is on by default; the URL only records an explicit opt-out.
  state.writeAudit = get('write_audit') !== '0';
  const o = state.overrides;
  if (GENDER_PREF_OPTIONS.includes(get('gender_pref')))
    o.teacher_gender_preference = get('gender_pref');
  if (STUDENT_GENDER_OPTIONS.includes(get('student_gender')))
    o.student_gender = get('student_gender');
  if (ETHNICITY_OPTIONS.includes(get('ethnicity'))) o.student_ethnicity = get('ethnicity');
  if (/^[A-Za-z]{2}$/.test(get('country'))) o.country_iso = get('country').toUpperCase();
  if (BOOL_PARAM[get('referral')]) o.is_referral_student = BOOL_PARAM[get('referral')];
  if (BOOL_PARAM[get('prenatal')]) o.is_prenatal_student = BOOL_PARAM[get('prenatal')];
  if (get('no_relax') === '1') o.disable_thin_pool_relaxation = true;
  if (q.has('goals')) {
    o.goals = [
      ...new Set(
        (q.get('goals') || '')
          .split(',')
          .map((g) => g.trim())
          .filter(Boolean)
      )
    ];
  }
  return state;
};

// Inverse of parseUrlState; defaults and "use student's data" fields are omitted.
export const buildUrlSearch = ({
  email,
  slotDate,
  slotTime,
  topN,
  phaseAOnly,
  writeAudit,
  overrides
}) => {
  const q = new URLSearchParams();
  const o = overrides || EMPTY_OVERRIDES;
  if (email && email.trim()) q.set('email', email.trim());
  if (validDate(slotDate || '')) q.set('date', slotDate);
  if (TIME_RE.test(slotTime || '')) q.set('time', slotTime);
  const n = parseInt(topN, 10);
  if (!Number.isNaN(n) && n >= 1 && n !== DEFAULT_TOP_N) q.set('top_n', String(n));
  if (phaseAOnly) q.set('phase_a', '1');
  if (!writeAudit) q.set('write_audit', '0');
  if (o.teacher_gender_preference) q.set('gender_pref', o.teacher_gender_preference);
  if (o.student_gender) q.set('student_gender', o.student_gender);
  if (o.student_ethnicity) q.set('ethnicity', o.student_ethnicity);
  if (o.country_iso && o.country_iso.trim()) q.set('country', o.country_iso.trim().toUpperCase());
  if (o.is_referral_student) q.set('referral', o.is_referral_student === 'true' ? '1' : '0');
  if (o.is_prenatal_student) q.set('prenatal', o.is_prenatal_student === 'true' ? '1' : '0');
  if (o.disable_thin_pool_relaxation) q.set('no_relax', '1');
  if (Array.isArray(o.goals)) q.set('goals', o.goals.join(','));
  const str = q.toString();
  return str ? `?${str}` : '';
};

// navigator.clipboard needs a secure context; fall back to a hidden textarea.
export const copyText = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    if (ok) resolve();
    else reject(new Error('Copy failed'));
  });
};
